import { auth, authReady, googleProvider, db } from '@/firebase'
import i18n from '@/i18n'
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  deleteUser,
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  updatePassword,
  updateProfile
} from 'firebase/auth'
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  query,
  serverTimestamp,
  setDoc,
  where,
  writeBatch
} from 'firebase/firestore'
import { getUserErrorMessage } from '@/utils/errors'
import { LEGAL_TERMS_VERSION, LEGAL_TEXT_VERSIONS } from '@/config/legal'

const state = {
  user: null,
  loading: true,
  error: null,
  inited: false,
  selectedCompanyId: localStorage.getItem("selectedCompanyId") || null,
  selectedCompanyName: localStorage.getItem("selectedCompanyName") || null
}

const getters = {
  isAuthenticated: state => !!state.user,
  isAuthed: state => !!state.user,
  inited: state => state.inited,
  user: state => state.user,
  authReady: state => !state.loading,
  authError: state => state.error,
  userRole: state => state.user?.role || null,
  isAdmin: state => state.user?.role === 'admin',
  isSuperAdmin: state => state.user?.role === 'superadmin',
  canAccessAdminPanel: state => ["admin", "superadmin"].includes(state.user?.role),
  canChangePassword: state => !!state.user?.providerIds?.includes("password"),
  userName: state =>
    state.user ? `${state.user.nombre || ''} ${state.user.apellidos || ''}`.trim() : '',
  companyId: state => state.user?.role === "superadmin"
    ? state.selectedCompanyId
    : state.user?.companyId || null,
  companyName: state => state.user?.role === "superadmin"
    ? state.selectedCompanyName
    : state.user?.companyName || null,
  selectedCompanyId: state => state.selectedCompanyId,
  selectedCompanyName: state => state.selectedCompanyName
}

const mutations = {
  SET_USER(state, user) { state.user = user },
  SET_LOADING(state, value) { state.loading = value },
  SET_ERROR(state, error) { state.error = error },
  SET_INITED(state, value) { state.inited = value },
  SET_SELECTED_COMPANY(state, company) {
    state.selectedCompanyId = company?.id || null;
    state.selectedCompanyName = company?.name || null;

    if (state.selectedCompanyId) {
      localStorage.setItem("selectedCompanyId", state.selectedCompanyId);
      localStorage.setItem("selectedCompanyName", state.selectedCompanyName || "");
    } else {
      localStorage.removeItem("selectedCompanyId");
      localStorage.removeItem("selectedCompanyName");
    }
  }
}

let authInitialized = false;
let initAuthPromise = null;
const RECENT_LOGIN_LIMIT_MS = 5 * 60 * 1000;

function getAuthErrorMessage(error) {
  return getUserErrorMessage(error, 'authErrors.default', i18n)
}

function assertRecentLogin(user) {
  const lastSignIn = user?.metadata?.lastSignInTime
    ? new Date(user.metadata.lastSignInTime).getTime()
    : 0;

  if (!lastSignIn || Date.now() - lastSignIn > RECENT_LOGIN_LIMIT_MS) {
    throw new Error(i18n.t('authErrors.recentLoginRequired'))
  }
}

function addDocsFromSnapshot(documentsByPath, snap) {
  snap.docs.forEach((documentSnapshot) => {
    documentsByPath.set(documentSnapshot.ref.path, documentSnapshot);
  });
}

async function deleteFirestoreDocuments(documentsByPath) {
  const docsToDelete = Array.from(documentsByPath.values());

  for (let i = 0; i < docsToDelete.length; i += 450) {
    const batch = writeBatch(db);
    docsToDelete.slice(i, i + 450).forEach((documentSnapshot) => {
      batch.delete(documentSnapshot.ref);
    });
    await batch.commit();
  }
}

async function getDocsIfAllowed(queryRef) {
  try {
    return await getDocs(queryRef);
  } catch (error) {
    if (error?.code === "permission-denied") return null;
    throw error;
  }
}

async function buildUserProfile(firebaseUser) {
  const ref = doc(db, "users", firebaseUser.uid);
  const snap = await getDoc(ref);
  let extra = snap.exists() ? snap.data() : null;

  if (!extra) {
    const invite = await getAdminInvite(firebaseUser.email);

    if (invite) {
      extra = {
        ...invite,
        email: firebaseUser.email,
        inviteAcceptedAt: new Date().toISOString(),
      };
      await setDoc(ref, extra);
      await deleteAdminInvite(firebaseUser.email);
    } else {
      extra = {};
    }
  } else if (extra.role === "admin") {
    await deleteAdminInvite(firebaseUser.email);
  }

  let companyName = null;
  if (extra.companyId) {
    const companyDoc = await getDoc(doc(db, "companies", extra.companyId));
    if (companyDoc.exists()) {
      companyName = companyDoc.data().name || null;
    }
  }

  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: `${extra.nombre || ''} ${extra.apellidos || ''}`.trim() || firebaseUser.displayName || '',
    photoURL: firebaseUser.photoURL,
    providerIds: firebaseUser.providerData.map((provider) => provider.providerId),
    companyName,
    ...extra
  };
}

function getEmailKey(email) {
  return (email || "").trim().toLowerCase();
}

async function getAdminInvite(email) {
  const emailKey = getEmailKey(email);
  if (!emailKey) return null;

  try {
    const inviteSnap = await getDoc(doc(db, "adminInvites", emailKey));
    return inviteSnap.exists() ? inviteSnap.data() : null;
  } catch (error) {
    if (error?.code === "permission-denied") return null;
    throw error;
  }
}

async function deleteAdminInvite(email) {
  const emailKey = getEmailKey(email);
  if (!emailKey) return;

  try {
    await deleteDoc(doc(db, "adminInvites", emailKey));
  } catch (error) {
    if (error?.code !== "permission-denied" && error?.code !== "not-found") {
      throw error;
    }
  }
}

const actions = {

  async initAuth({ commit }) {
    if (authInitialized) return initAuthPromise;
    authInitialized = true;

    commit("SET_LOADING", true);

    initAuthPromise = authReady.then(() => new Promise((resolve) => {
      onAuthStateChanged(auth, (firebaseUser) => {
        const userProfilePromise = firebaseUser
          ? buildUserProfile(firebaseUser)
          : Promise.resolve(null);

        userProfilePromise
          .then((userProfile) => {
            commit("SET_USER", userProfile);
            commit("SET_ERROR", null);
          })
          .catch((e) => {
            commit("SET_USER", null);
            commit("SET_ERROR", getAuthErrorMessage(e));
          })
          .finally(() => {
            commit("SET_LOADING", false);
            commit("SET_INITED", true);
            resolve();
          });
      });
    }));

    return initAuthPromise;
  },

  // LOGIN email/pass
  async login({ commit }, { email, password }) {
    commit("SET_ERROR", null);

    try {
      const res = await signInWithEmailAndPassword(auth, email, password);
      commit("SET_USER", await buildUserProfile(res.user));

    } catch (e) {
      commit("SET_ERROR", getAuthErrorMessage(e));
    }
  },

  async resetPassword({ commit }, email) {
    commit("SET_ERROR", null);

    try {
      await sendPasswordResetEmail(auth, email);
    } catch (e) {
      commit("SET_ERROR", getAuthErrorMessage(e));
      throw e;
    }
  },

  async changePassword({ commit, state }, { currentPassword, newPassword }) {
    commit("SET_ERROR", null);

    try {
      const firebaseUser = auth.currentUser;
      const email = state.user?.email || firebaseUser?.email || "";

      if (!firebaseUser || !email) {
        throw new Error(i18n.t("errors.unauthenticated"));
      }

      const credential = EmailAuthProvider.credential(email, currentPassword);
      await reauthenticateWithCredential(firebaseUser, credential);
      await updatePassword(firebaseUser, newPassword);
    } catch (e) {
      commit("SET_ERROR", getAuthErrorMessage(e));
      throw e;
    }
  },

  // REGISTER
  async register({ commit }, { email, password, nombre, apellidos, role = "user" }) {
    commit("SET_ERROR", null);
    try {
      const res = await createUserWithEmailAndPassword(auth, email, password);
      const user = res.user;

      await updateProfile(user, { displayName: `${nombre} ${apellidos}` });

      const data = {
        nombre,
        apellidos,
        email,
        role,
        createdAt: new Date().toISOString(),
        legalAcceptedAt: serverTimestamp(),
        legalAcceptedVersion: LEGAL_TERMS_VERSION,
        legalAcceptedTextVersions: LEGAL_TEXT_VERSIONS,
      };

      await setDoc(doc(db, "users", user.uid), data);

      commit("SET_USER", {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        companyName: null,   // Registrado → aún no está en ninguna empresa
        ...data,
      });

    } catch (e) {
      commit("SET_ERROR", getAuthErrorMessage(e));
    }
  },

  // GOOGLE LOGIN
  async loginWithGoogle({ commit }) {
    commit("SET_ERROR", null);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      const ref = doc(db, "users", user.uid);
      const snap = await getDoc(ref);

      let data;
      if (!snap.exists()) {
        const invite = await getAdminInvite(user.email);

        if (invite) {
          data = {
            ...invite,
            email: user.email,
            inviteAcceptedAt: new Date().toISOString(),
            legalAcceptedAt: serverTimestamp(),
            legalAcceptedVersion: LEGAL_TERMS_VERSION,
            legalAcceptedTextVersions: LEGAL_TEXT_VERSIONS,
          };
        } else {
          await deleteUser(user);
          throw Object.assign(new Error("Google account is not allowed on the web panel."), {
            code: "konvenio/no-manager-access",
          });
        }

        await setDoc(ref, data);
        if (invite) await deleteAdminInvite(user.email);
      } else {
        data = snap.data();
        if (data.role === "admin") await deleteAdminInvite(user.email);
      }

      // 🔥 Cargar nombre empresa si existe
      let companyName = null;
      if (data.companyId) {
        const companyDoc = await getDoc(doc(db, "companies", data.companyId));
        if (companyDoc.exists()) {
          companyName = companyDoc.data().name || null;
        }
      }

      commit("SET_USER", {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        companyName,        // <--- NUEVO
        ...data,
      });

    } catch (e) {
      commit("SET_ERROR", getAuthErrorMessage(e));
    }
  },

  async logout({ commit }) {
    await signOut(auth);
    commit("SET_USER", null);
    commit("SET_SELECTED_COMPANY", null);
  },

  async deleteAccount({ commit, state }) {
    commit("SET_ERROR", null);

    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) return;

      assertRecentLogin(firebaseUser);

      const uid = firebaseUser.uid;
      const email = state.user?.email || firebaseUser.email || "";
      const documentsByPath = new Map();

      const queries = [
        query(collection(db, "companyJoinRequests"), where("uid", "==", uid)),
        query(collection(db, "suggestions"), where("userId", "==", uid)),
        query(collection(db, "suggestions"), where("createdByUid", "==", uid)),
      ];

      if (email) {
        const adminInviteRef = doc(db, "adminInvites", getEmailKey(email));
        try {
          const adminInviteSnap = await getDoc(adminInviteRef);
          if (adminInviteSnap.exists()) {
            documentsByPath.set(adminInviteRef.path, adminInviteSnap);
          }
        } catch (error) {
          if (error?.code !== "permission-denied") throw error;
        }

        queries.push(
          query(collection(db, "companyJoinRequests"), where("email", "==", email)),
          query(collection(db, "requests"), where("email", "==", email)),
          query(collection(db, "suggestions"), where("email", "==", email)),
          query(collection(db, "suggestions"), where("userEmail", "==", email))
        );
      }

      const snaps = await Promise.all(queries.map((q) => getDocsIfAllowed(q)));
      snaps.filter(Boolean).forEach((snap) => addDocsFromSnapshot(documentsByPath, snap));

      documentsByPath.set(`users/${uid}`, {
        ref: doc(db, "users", uid),
      });
      await deleteFirestoreDocuments(documentsByPath);
      await deleteUser(firebaseUser);
      commit("SET_USER", null);
      commit("SET_SELECTED_COMPANY", null);
    } catch (e) {
      commit("SET_ERROR", getAuthErrorMessage(e));
      throw e;
    }
  },

  setAuthError({ commit }, error) {
    commit("SET_ERROR", error);
  },

  setSelectedCompany({ commit }, company) {
    commit("SET_SELECTED_COMPANY", company);
  }
};

export default { namespaced: true, state, getters, mutations, actions };
